import {
  platformPermissions as publicPlatformPermissions,
  type PlatformAuditEvent as PublicPlatformAuditEvent,
  type PlatformBootstrap,
  type PlatformCustomer,
  type PlatformHumanObservationDecision,
  type PlatformHumanObservationQueueItem,
  type PlatformPermission as PublicPlatformPermission,
  type ValidationState
} from "../../src/contracts/platform-admin";

export const platformPermissions = publicPlatformPermissions;
export type PlatformPermission = PublicPlatformPermission;
export type AdminBootstrap = PlatformBootstrap;

export type AdminRoute =
  | Readonly<{ page: "overview" }>
  | Readonly<{ page: "customers" }>
  | Readonly<{ page: "customer"; customerKey: string }>
  | Readonly<{ page: "labs" }>
  | Readonly<{ page: "lab"; customerKey: string }>
  | Readonly<{ page: "validation" }>
  | Readonly<{ page: "releases" }>
  | Readonly<{ page: "support" }>
  | Readonly<{ page: "audit" }>
  | Readonly<{ page: "health" }>
  | Readonly<{ page: "not_found" }>;

export type PlatformAuditEvent = PublicPlatformAuditEvent;

export type HumanObservationQueueItem = PlatformHumanObservationQueueItem & Readonly<{
  support_launch_url?: string;
}>;
export type HumanObservationDecision = PlatformHumanObservationDecision;

export type PlatformReadiness = Readonly<{
  status: "ready" | "unavailable";
  release?: string;
  dependencies: Readonly<Record<string, string>>;
  capabilities: Readonly<Record<string, string>>;
}>;

export type ValidationEvidenceRow = Readonly<{
  customerKey: string;
  customerName: string;
  featureId: string;
  label: string;
  domainId: string;
  capabilityKey: string;
  state: ValidationState;
  evidence: string;
}>;

const customerKeyPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/u;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const sha256Pattern = /^[0-9a-f]{64}$/iu;
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
const safeDecode = (value: string): string => {
  try { return decodeURIComponent(value); } catch { return ""; }
};

export const parseAdminRoute = (hash: string): AdminRoute => {
  const path = hash.replace(/^#/u, "").split("?")[0] ?? "";
  const parts = path.split("/").filter(Boolean).map(safeDecode);
  if (parts.length === 0 || (parts.length === 1 && parts[0] === "overview")) return { page: "overview" };
  if (parts.length === 1 && parts[0] === "customers") return { page: "customers" };
  if (parts.length === 2 && parts[0] === "customers" && customerKeyPattern.test(parts[1] ?? "")) {
    return { page: "customer", customerKey: parts[1] ?? "" };
  }
  if (parts.length === 3 && parts[0] === "customers" && parts[2] === "lab" && customerKeyPattern.test(parts[1] ?? "")) {
    return { page: "lab", customerKey: parts[1] ?? "" };
  }
  if (parts.length === 1 && parts[0] === "labs") return { page: "labs" };
  if (parts.length === 2 && parts[0] === "labs" && customerKeyPattern.test(parts[1] ?? "")) {
    return { page: "lab", customerKey: parts[1] ?? "" };
  }
  if (parts.length === 1 && ["validation", "releases", "support", "audit", "health"].includes(parts[0] ?? "")) {
    return { page: parts[0] as "validation" | "releases" | "support" | "audit" | "health" };
  }
  return { page: "not_found" };
};

export const routeHash = (route: Exclude<AdminRoute, { page: "not_found" }>): string => {
  if (route.page === "customer") return `#/customers/${encodeURIComponent(route.customerKey)}`;
  if (route.page === "lab") return `#/customers/${encodeURIComponent(route.customerKey)}/lab`;
  return `#/${route.page}`;
};

export const permissionForRoute = (route: AdminRoute): PlatformPermission | undefined => {
  if (route.page === "not_found") return undefined;
  if (route.page === "labs" || route.page === "lab" || route.page === "releases") return "PERM-001-02";
  if (route.page === "support") return "PERM-001-03";
  return "PERM-001-01";
};

export const hasPermission = (owner: AdminBootstrap["owner"], permission: PlatformPermission): boolean =>
  owner.permissions.includes(permission);

export const parseAdminBootstrap = (value: unknown): AdminBootstrap => {
  if (!isRecord(value) || !isRecord(value.owner) || typeof value.owner.email !== "string"
    || typeof value.owner.displayName !== "string" || value.owner.role !== "super_admin"
    || !Array.isArray(value.owner.permissions) || !Array.isArray(value.customers)
    || typeof value.release !== "string") throw new Error("The platform bootstrap response is invalid.");
  const permissions = value.owner.permissions.filter((item): item is PlatformPermission =>
    typeof item === "string" && platformPermissions.includes(item as PlatformPermission));
  if (permissions.length !== value.owner.permissions.length || new Set(permissions).size !== permissions.length) {
    throw new Error("The platform bootstrap permissions are invalid.");
  }
  return {
    owner: { email: value.owner.email, displayName: value.owner.displayName, role: "super_admin", permissions },
    release: value.release,
    customers: value.customers.map(parsePlatformCustomer)
  };
};

export const parsePlatformCustomer = (value: unknown): PlatformCustomer => {
  if (!isRecord(value) || typeof value.key !== "string" || !customerKeyPattern.test(value.key)
    || typeof value.name !== "string" || !isRecord(value.instance) || !isRecord(value.projection)
    || !Array.isArray(value.features) || !Array.isArray(value.enabledCapabilities)
    || !Array.isArray(value.integrations) || !isRecord(value.health) || !isRecord(value.lab)) {
    throw new Error("The customer assurance response is invalid.");
  }
  return value as PlatformCustomer;
};

export const parseAuditEvents = (value: unknown): readonly PlatformAuditEvent[] => {
  if (!isRecord(value) || !Array.isArray(value.items)) throw new Error("The audit response is invalid.");
  return value.items.map((item) => {
    if (!isRecord(item) || typeof item.id !== "string" || typeof item.action !== "string"
      || typeof item.targetType !== "string" || typeof item.outcome !== "string"
      || !["allowed", "denied", "unavailable"].includes(item.outcome)
      || typeof item.correlationId !== "string" || typeof item.occurredAt !== "string"
      || (item.actorEmail !== null && typeof item.actorEmail !== "string")
      || (item.targetKey !== null && typeof item.targetKey !== "string")) throw new Error("The audit response is invalid.");
    return item as PlatformAuditEvent;
  });
};

export const parseHumanObservationQueue = (value: unknown): readonly HumanObservationQueueItem[] => {
  if (!isRecord(value) || !Array.isArray(value.items)) throw new Error("The support-routing response is invalid.");
  return value.items.map((item) => {
    if (!isRecord(item) || typeof item.observation_request_id !== "string" || !uuidPattern.test(item.observation_request_id)
      || typeof item.tenant_id !== "string" || !uuidPattern.test(item.tenant_id)
      || typeof item.instance_id !== "string" || !uuidPattern.test(item.instance_id)
      || typeof item.route_sha256 !== "string" || !sha256Pattern.test(item.route_sha256)
      || typeof item.state !== "string" || typeof item.importance !== "string" || typeof item.risk !== "string"
      || typeof item.business_blocking !== "boolean" || typeof item.requested_at !== "string"
      || typeof item.updated_at !== "string" || typeof item.support_identity_ready !== "boolean"
      || typeof item.lifecycle_delivery_ready !== "boolean"
      || (item.scope_sha256 !== undefined && (typeof item.scope_sha256 !== "string" || !sha256Pattern.test(item.scope_sha256)))
      || (item.scope_expires_at !== undefined && typeof item.scope_expires_at !== "string")
      || (item.decision_state !== undefined && typeof item.decision_state !== "string")
      || (item.support_launch_url !== undefined && typeof item.support_launch_url !== "string")) {
      throw new Error("The support-routing response is invalid.");
    }
    return item as HumanObservationQueueItem;
  });
};

export const parseHumanObservationDecision = (value: unknown): HumanObservationDecision => {
  if (!isRecord(value) || typeof value.decision_id !== "string" || !uuidPattern.test(value.decision_id)
    || !["activate", "revoke", "resolve"].includes(String(value.operation))
    || !["pending", "applied", "reconciliation_required", "expired"].includes(String(value.state))) {
    throw new Error("The support-decision response is invalid.");
  }
  return value as HumanObservationDecision;
};

export const parsePlatformReadiness = (value: unknown): PlatformReadiness => {
  if (!isRecord(value) || !["ready", "unavailable"].includes(String(value.status))
    || !isRecord(value.dependencies) || !isRecord(value.capabilities)) {
    throw new Error("The platform-readiness response is invalid.");
  }
  const stringEntries = (record: Record<string, unknown>): Readonly<Record<string, string>> =>
    Object.fromEntries(Object.entries(record).filter((entry): entry is [string, string] => typeof entry[1] === "string"));
  const capabilityEntries = (record: Record<string, unknown>): Readonly<Record<string, string>> => Object.fromEntries(
    Object.entries(record).flatMap(([key, candidate]) => {
      if (typeof candidate === "string") return [[key, candidate] as const];
      if (isRecord(candidate) && typeof candidate.state === "string") {
        const adapter = typeof candidate.adapter === "string" ? ` · ${candidate.adapter}` : "";
        return [[key, `${candidate.state}${adapter}`] as const];
      }
      return [];
    })
  );
  return {
    status: value.status as PlatformReadiness["status"],
    ...(typeof value.release === "string" ? { release: value.release } : {}),
    dependencies: stringEntries(value.dependencies),
    capabilities: capabilityEntries(value.capabilities)
  };
};

export const customerCoverage = (customer: PlatformCustomer): Readonly<{ validated: number; executable: number; total: number }> => ({
  validated: customer.features.filter((feature) => feature.state === "validated" || feature.state === "production_ready").length,
  executable: customer.features.filter((feature) => feature.state === "executable").length,
  total: customer.features.length
});

export const overviewSummary = (data: AdminBootstrap): Readonly<{
  customers: number;
  activeCustomers: number;
  unknownHealth: number;
  readyLabs: number;
  recordedFeatures: number;
}> => ({
  customers: data.customers.length,
  activeCustomers: data.customers.filter((customer) => customer.state === "active").length,
  unknownHealth: data.customers.filter((customer) => customer.health.state === "unknown").length,
  readyLabs: data.customers.filter((customer) => labUnavailableReason(customer) === undefined).length,
  recordedFeatures: data.customers.reduce((total, customer) => total + customer.features.length, 0)
});

export const labUnavailableReason = (customer: PlatformCustomer): string | undefined => {
  if (customer.key !== "martin") return "A Product Lab runtime is not registered for this customer.";
  if (customer.lab.state !== "ready") return "Product Lab provisioning has not reached ready state.";
  if (customer.lab.mode !== "domain_service_replay") return "This owner console supports only the current domain-service replay milestone.";
  if (customer.lab.usesProductionCommandHandlers || customer.lab.exactConfigurationTwin) {
    return "The registry claims a runtime mode that this seed-only console is not authorized to open.";
  }
  return undefined;
};

export const validationEvidence = (data: AdminBootstrap): readonly ValidationEvidenceRow[] =>
  data.customers.flatMap((customer) => customer.features.map((feature) => ({
    customerKey: customer.key,
    customerName: customer.name,
    featureId: feature.id,
    label: feature.label,
    domainId: feature.domainId,
    capabilityKey: feature.capabilityKey,
    state: feature.state,
    evidence: feature.evidence
  })));

export const supportApprovalUnavailableReason = (item: HumanObservationQueueItem): string | undefined => {
  if (!item.lifecycle_delivery_ready) return "No exact customer lifecycle delivery route is provisioned.";
  if (!item.support_identity_ready) return "The tenant-scoped support identity is not ready.";
  if (item.scope_sha256 === undefined) return "The customer has not registered an exact support scope.";
  if (item.scope_expires_at !== undefined && Date.parse(item.scope_expires_at) <= Date.now()) return "The registered support scope has expired.";
  if (item.decision_state === "activation_authorized") return "This request is already activated.";
  if (item.state !== "requested") return "Only a currently requested observation may be approved.";
  return undefined;
};

export const safeSupportLaunchUrl = (item: HumanObservationQueueItem): string | undefined => {
  if (item.decision_state !== "activation_authorized" || item.support_launch_url === undefined) return undefined;
  try {
    const url = new URL(item.support_launch_url);
    return url.protocol === "https:" && url.username === "" && url.password === "" ? url.toString() : undefined;
  } catch { return undefined; }
};
